import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { listAdmins, addAdmin, removeAdmin, ADMIN_ROLES, type AdminRole, type AdminRow } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { cn, formatTimestamp } from "@/lib/utils";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./admin.css";

const shortDate = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

type EditorState = { mode: "new" } | { mode: "edit"; row: AdminRow };

export function AdminsWorkspace() {
  const readOnly = usePreviewReadOnly();
  const qc = useQueryClient();
  const me = useAuthStore(s => s.me);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const query = useQuery({ queryKey: ["admins"], queryFn: listAdmins });
  const remove = useMutation({ mutationFn: (id: string) => removeAdmin(id), onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }) });

  function handleRemove(row: AdminRow) {
    if (!confirm(`Remove ${row.global_name ?? row.username ?? row.discord_id} from the admin whitelist?`)) return;
    remove.mutate(row.discord_id, { onSuccess: () => setEditor(e => e?.mode === "edit" && e.row.discord_id === row.discord_id ? null : e) });
  }

  const isBoot = me?.role === "internal";
  const envAdmins = query.data?.envAdmins ?? [];
  const dbAdmins = query.data?.admins ?? [];
  // The server already filters env admins out of `admins` for non-internal viewers; internal viewers split them locally.
  const envSet = new Set(envAdmins);
  const visibleDbAdmins = isBoot ? dbAdmins.filter(a => !envSet.has(a.discord_id)) : dbAdmins;
  const seenEnvAdmins = isBoot ? dbAdmins.filter(a => envSet.has(a.discord_id)) : [];

  return <WorkspaceShell crumbs={[{ label: "Administration" }, { label: "Admins" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page is-narrow">
      <div className="ws-head"><h1>Admins</h1>
        <div className="ws-head-actions"><button className="pw-button pw-button-primary" onClick={() => setEditor({ mode: "new" })} disabled={editor?.mode === "new"}><Plus size={14} />New Admin</button></div>
      </div>
      {editor && <AdminEditor key={editor.mode === "edit" ? editor.row.discord_id : "new"} state={editor} canSave={isBoot && !readOnly}
        existingIds={dbAdmins.map(a => a.discord_id)} envAdmins={envAdmins} myDiscordId={me?.discordId ?? null} onClose={() => setEditor(null)}
        onSaved={() => { setEditor(null); qc.invalidateQueries({ queryKey: ["admins"] }); }} />}
      {query.error && <p className="ws-alert" role="alert">{String(query.error)}</p>}
      {isBoot && envAdmins.length > 0 && <section className="ws-card is-flush ad-card-flush">
        <div className="ws-card-head"><h2>Bootstrap admins <span className="pw-badge">env</span></h2><span className="ws-muted" title="Remove from ADMIN_DISCORD_IDS to revoke">Always allowed</span></div>
        {seenEnvAdmins.length ? <AdminTable rows={seenEnvAdmins} envAdmins={envAdmins} myDiscordId={me?.discordId ?? null} envOnly />
          : <p className="ws-empty">{envAdmins.length} configured · none signed in yet</p>}
      </section>}
      <section className="ws-card is-flush ad-card-flush">
        <div className="ws-card-head"><h2>Whitelist</h2><span className="ws-muted">{visibleDbAdmins.length} entr{visibleDbAdmins.length === 1 ? "y" : "ies"}</span></div>
        {query.isLoading ? <div className="ws-empty" role="status"><span className="pw-loading-dot" /> Loading admins…</div>
          : visibleDbAdmins.length === 0 ? <p className="ws-empty">No additional admins yet.</p>
          : <AdminTable rows={visibleDbAdmins} envAdmins={envAdmins} myDiscordId={me?.discordId ?? null} disabled={remove.isPending || readOnly}
              onEdit={row => setEditor({ mode: "edit", row })} onRemove={isBoot ? handleRemove : undefined} />}
        {remove.error && <p className="ws-alert ad-card-error" role="alert">{String(remove.error)}</p>}
      </section>
    </div></div>
  </WorkspaceShell>;
}

/** Add or edit a whitelist entry. The API upserts on POST, so editing re-posts the same Discord ID with the new note. */
function AdminEditor({ state, canSave, existingIds, envAdmins, myDiscordId, onClose, onSaved }: { state: EditorState; canSave: boolean; existingIds: string[]; envAdmins: string[]; myDiscordId: string | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = state.mode === "edit";
  const [discordId, setDiscordId] = useState(isEdit ? state.row.discord_id : "");
  const [note, setNote] = useState(isEdit ? state.row.note ?? "" : "");
  const [role, setRole] = useState<AdminRole>(isEdit ? state.row.role : "guest");
  const isSelf = isEdit && state.row.discord_id === myDiscordId;
  const [formError, setFormError] = useState<string | null>(null);
  const id = discordId.trim();
  const validId = /^\d+$/.test(id);
  const save = useMutation({
    mutationFn: async () => {
      if (!validId) throw new Error("Discord ID must be digits only.");
      if (!isEdit && envAdmins.includes(id)) throw new Error("This user is already a bootstrap admin.");
      if (!isEdit && existingIds.includes(id)) throw new Error("This user is already on the whitelist.");
      await addAdmin(id, note.trim() || undefined, role);
    },
    onError: e => setFormError(String((e as Error).message ?? e)),
    onSuccess: onSaved,
  });
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return; // hard guard — server also returns 403
    setFormError(null);
    save.mutate();
  }
  const name = isEdit ? state.row.global_name ?? state.row.username : null;

  return <form onSubmit={handleSubmit} className="ws-card ws-form" aria-label={isEdit ? `Edit admin ${state.row.discord_id}` : "New admin"}>
    <div className="ws-card-head ad-editor-head"><h2>{isEdit ? <>Edit {name ?? <span className="ad-mono">{state.row.discord_id}</span>}</> : "New admin"}</h2>
      <button type="button" className="pw-icon-button" aria-label="Close editor" onClick={onClose} disabled={save.isPending}><X size={15} /></button></div>
    <label className="ws-field">Discord user ID
      <input className="ad-id" inputMode="numeric" value={discordId} onChange={e => setDiscordId(e.target.value)} disabled={isEdit} placeholder="e.g. 123456789012345678" autoFocus={!isEdit} />
      {!isEdit && (id && !validId ? <span className="ad-hint">Digits only</span>
        : <span className="ad-muted">Discord Developer Mode → right-click the user → Copy User ID</span>)}
    </label>
    <label className="ws-field">Note
      <input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional, e.g. role" autoFocus={isEdit} />
    </label>
    <fieldset className="ws-field ad-roles" disabled={isSelf}>
      <legend>Role{isSelf && <span className="ad-muted"> · you can't change your own role</span>}</legend>
      {ADMIN_ROLES.map(r => <label key={r.value} className={cn("ad-role", role === r.value && "is-selected")}>
        <input type="radio" name="admin-role" value={r.value} checked={role === r.value} onChange={() => setRole(r.value)} />
        <span><strong>{r.label}</strong><span className="ad-muted">{r.description}</span></span>
      </label>)}
    </fieldset>
    {isEdit && <span className="ad-muted ad-meta">Added {formatTimestamp(state.row.added_at)}{state.row.added_by ? <> by <span className="ad-mono">{state.row.added_by}</span></> : null}</span>}
    {formError && <p className="ws-alert" role="alert">{formError}</p>}
    <div className="ad-actions">
      <button type="button" className="pw-button" onClick={onClose} disabled={save.isPending}>Cancel</button>
      <button type="submit" className="pw-button pw-button-primary" disabled={!canSave || !validId || save.isPending}
        title={canSave ? undefined : "Only internal admins can change the whitelist"}>{save.isPending ? "Saving…" : isEdit ? "Save changes" : "Add admin"}</button>
    </div>
  </form>;
}

function AdminTable({ rows, envAdmins, myDiscordId, envOnly, disabled, onEdit, onRemove }: { rows: AdminRow[]; envAdmins: string[]; myDiscordId: string | null; envOnly?: boolean; disabled?: boolean; onEdit?: (row: AdminRow) => void; onRemove?: (row: AdminRow) => void }) {
  const envSet = new Set(envAdmins);
  return <div className="ws-table ad-table" role="table">
    <div className="ws-table-row ws-table-head ad-admin-row" role="row">{["Discord ID", "Name", "Role", "Added", "Added by", "Note", ""].map((h, i) => <span role="columnheader" key={i}>{h}</span>)}</div>
    {rows.map(r => {
      const isMe = r.discord_id === myDiscordId;
      const isEnv = envSet.has(r.discord_id);
      const name = r.global_name ?? r.username;
      return <div key={r.discord_id} className="ws-table-row ad-admin-row" role="row">
        <span role="cell" className="ad-main"><span className="ad-mono">{r.discord_id} {isMe && <span className="pw-badge">you</span>}</span></span>
        <span role="cell">{name ?? <em className="ad-muted">not signed in yet</em>}</span>
        <span role="cell"><RoleBadge role={isEnv ? "internal" : r.role} /></span>
        <span role="cell" className="ad-muted" title={formatTimestamp(r.added_at)}>{shortDate.format(r.added_at)}</span>
        <span role="cell" className="ad-mono ad-muted" data-label="by">{r.added_by ?? "—"}</span>
        <span role="cell" className="ad-muted ad-wide" title={r.note ?? ""}>{r.note ?? "—"}</span>
        <span role="cell" className="ad-end">
          {onEdit && !envOnly && !isEnv && <button className="pw-icon-button" title="Edit" aria-label={`Edit ${name ?? r.discord_id}`} onClick={() => onEdit(r)}><Pencil size={14} /></button>}
          {onRemove && !envOnly && !isEnv && !isMe &&
            <button className="pw-icon-button ad-remove" title="Remove" aria-label={`Remove ${name ?? r.discord_id}`} disabled={disabled} onClick={() => onRemove(r)}><Trash2 size={14} /></button>}</span>
        <span className="ad-break" aria-hidden="true" />
      </div>;
    })}
  </div>;
}

function RoleBadge({ role }: { role: AdminRole }) {
  const label = ADMIN_ROLES.find(r => r.value === role)?.label ?? role;
  return <span className={cn("pw-badge", `ad-role-${role}`)}>{label}</span>;
}
